// Shared chrome for the 13 slide previews — the frozen frame pieces every v15 slide carries
// (eyebrow with gold tick, NAVEN lockup, page number, hairlines) plus the px-placement helper
// and the deck's own date/CHF formatters.
//
// DOCUMENT, NOT UI: everything rendered here is part of the frozen deck design (golden master
// LCH01 v15) in the deck's language — it deliberately does NOT go through useT, exactly like
// the SOW HTML. The operator-facing editor strings live in DecksPage.tsx and are translated.
// Geometry is inline px straight out of v15-geometry.json (1280×720 space); typography comes
// from public/styles/deck-preview.css (.dkp-*).
import type { CSSProperties, ReactNode } from 'react';
import { parseEmphasis } from '../../../../shared/deckLogic.js';
import { imageFrameOf, splitXOf } from '../../../../shared/deckTypes.js';
export { splitXOf };

/** The four phase colours, by GanttBoundRow.phaseIndex (light teal, gold, dark teal, black) —
 *  MUST match PHASE_BAR/PHASE_TEXT in the server's deckTemplates/helpers.ts, or the preview
 *  and the exported PPTX colour the same phase differently. Order verified against the v15
 *  golden master's gantt (bars in time order: 7FADA9 → C8A96E → 2E5F5C → 2A2C30). */
export const PHASE_FILL = ['#7FADA9', '#C8A96E', '#2E5F5C', '#2A2C30'] as const;
/** The readable text twin of each phase colour (refs, labels). */
export const PHASE_TEXT = ['#3F7B78', '#8A6F3E', '#2E5F5C', '#121316'] as const;

/** The slide root. `variant` picks the frozen ground: paper (default), dark, or photo. */
export function Dkp({ variant = 'paper', children }: {
  variant?: 'paper' | 'dark' | 'photo';
  children: ReactNode;
}) {
  const cls = variant === 'dark' ? 'dkp dkp--dark' : variant === 'photo' ? 'dkp dkp--photo' : 'dkp';
  return <div className={cls}>{children}</div>;
}

/** Absolute placement in the 1280×720 px space. Omitted w/h leave the box auto-sized. */
export function Abs({ x, y, w, h, style, className, children }: {
  x: number; y: number; w?: number; h?: number;
  style?: CSSProperties; className?: string; children?: ReactNode;
}) {
  return (
    <div
      className={className ? `dkp-abs ${className}` : 'dkp-abs'}
      style={{ left: x, top: y, width: w, height: h, ...style }}
    >
      {children}
    </div>
  );
}

/** The mono eyebrow with its 22px gold tick, at the v15 anchor (64, 56). */
export function Eyebrow({ text, x = 64, y = 56 }: { text: string; x?: number; y?: number }) {
  if (!text) return null;
  return (
    <Abs x={x} y={y}>
      <span className="dkp-eyebrow">{text}</span>
    </Abs>
  );
}

/** NAVEN lockup bottom-left (15px Geist 600, gold dot); `light` on dark/photo grounds. */
export function Brand({ light }: { light?: boolean } = {}) {
  return <span className="dkp-brand" style={light ? { color: '#F3F1EC' } : undefined}>NAVEN</span>;
}

/**
 * A cover-fit photograph with the slot's framing: focal point via object-position, zoom via a
 * transform about that same point — the CSS twin of deckLogic.frameImage (the PPTX crop).
 */
export function FramedImg({ src, content, slotKey, className, style }: {
  src: string;
  content: Record<string, unknown>;
  slotKey: string;
  className?: string;
  style?: CSSProperties;
}) {
  const f = imageFrameOf(content, slotKey);
  const z = Math.min(3, Math.max(1, f.zoom / 100));
  return (
    <img
      src={src}
      alt=""
      className={className}
      style={{
        width: '100%', height: '100%', objectFit: 'cover',
        objectPosition: `${f.fx}% ${f.fy}%`,
        transform: z !== 1 ? `scale(${z})` : undefined,
        transformOrigin: `${f.fx}% ${f.fy}%`,
        ...style,
      }}
    />
  );
}

/** Prose with **bold** emphasis (Geist Black in the master) — the preview twin of proseRuns. */
export function Emph({ text, boldColor }: { text: string; boldColor?: string }) {
  return (
    <>
      {parseEmphasis(text).map((r, i) => r.bold
        ? <strong key={i} style={{ fontWeight: 900, ...(boldColor ? { color: boldColor } : {}) }}>{r.text}</strong>
        : <span key={i}>{r.text}</span>)}
    </>
  );
}

/** Page number bottom-right; `light` when it sits on a photograph or dark ground. */
export function PageNo({ n, light }: { n: number | null | undefined; light?: boolean }) {
  if (n == null) return null;
  return <span className="dkp-pageno" style={light ? { color: '#F3F1EC' } : undefined}>{n}</span>;
}

/** A horizontal hairline. */
export function Rule({ x, y, w, color = '#D9D6CF', h = 1 }: {
  x: number; y: number; w: number; color?: string; h?: number;
}) {
  return <Abs x={x} y={y} w={w} h={h} style={{ background: color }} />;
}

/** 'YYYY-MM-DD' → '31.10.2026' (the tabelle voice). Anything unparsable passes through. */
export function fmtDot(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** 'YYYY-MM-DD' → '31.10.26' (the gantt/mesh voice). */
export function fmtDotShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : iso;
}

/** 89424 → "CHF 89'424" — the deck's Swiss thousands apostrophe, straight quote as in v15. */
export function chf(n: number): string {
  return `CHF ${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")}`;
}
